/**
 * Switcher state picker — same-module function declaration so the
 * llui v0.5.x cross-file walker traces through it.
 *
 * Lifted from a previous `accessor: (s: Model) => SwitcherState`
 * parameter on `viewCampaignSwitcher`. The function-parameter-
 * callback shape is the canonical anti-pattern the
 * `llui/opaque-accessor-file-wide-mask` warning flags — accessor
 * parameters cannot be resolved by the analyzer, so every reactive
 * accessor that delegates through them taints the host component's
 * `__prefixes` table with the whole-state sentinel.
 *
 * The pattern instead: declare a `<Shape>` interface that captures
 * the structural fields the view needs; export a bare-Identifier
 * function declaration the cross-file walker resolves; constrain
 * the view's `Model` parameter to `extends SwitcherStateShape` so
 * the type system enforces alignment without an import cycle. The
 * `currentId` field is sourced from `currentCampaignId()` at the
 * call site (a module-scoped side-effect read of `localStorage`),
 * which the view receives via a separate `currentId` slot on its
 * `SwitcherSlice`.
 */
import type { CampaignSummary } from './api.js';

export interface SwitcherStateShape {
  campaignsBoot:
    | { kind: 'picker'; campaigns: readonly CampaignSummary[] }
    | { kind: 'empty' | 'loading' | 'error' };
}

export interface SwitcherSlice {
  readonly campaigns: readonly CampaignSummary[];
  /** Current campaign id (may not match any entry while a switch is in flight). */
  readonly currentId: string | null;
}

/**
 * Resolve the campaigns list for the switcher.
 * Function declaration (not const-arrow) so the cross-file walker
 * follows the reads as a tracked container.
 */
export function pickSwitcherCampaigns<S extends SwitcherStateShape>(
  s: S,
): readonly CampaignSummary[] {
  return s.campaignsBoot.kind === 'picker' ? s.campaignsBoot.campaigns : [];
}
